import { Button, Card, Flex, Popconfirm, Tag } from 'antd';
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';
import React from 'react';
import { useTranslation } from 'react-i18next';
import AddressHelpers from '../../services/helpers/AddressHelpers';
import { CompanyCardStatus, deriveCompanyCardStatus } from '../../hooks/onboarding/companyCardState';
import TestRestaurantTag from '../provider-billing/TestRestaurantTag';

/**
 * `onContinueSetup(companyId)` is supplied by the PLG companies grid (it owns
 * the single `useOpenOnboarding` instance for the whole list — instantiating the
 * api client per card would allocate every Manager once per restaurant).
 */
export default function CompanyCard({
  company,
  isDraft,
  onEdit,
  onDelete,
  showOnboardingStatus = false,
  onContinueSetup = null,
}) {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Company:CompanyCard' });

  // Onboarding card state (FOO-6351 C7), derived from the shared progress model
  // (see companyCardState). Drafts are in-memory pre-create, so they carry no
  // onboarding state — they keep the create-flow check/close badges below.
  // `showOnboardingStatus` is only true on PLG-enabled provider views — master
  // providers and non-flagged partners see a plain card with no status badge.
  const status = showOnboardingStatus ? deriveCompanyCardStatus(company, { isDraft }) : null;

  return (
    <Card
      title={company.name}
      extra={
        <Flex gap="8px" align="center">
          <TestRestaurantTag company={company} />
          {status === CompanyCardStatus.Live && (
            <Tag color="success" data-testid="company-card-status-live">
              {t('labels.live')}
            </Tag>
          )}
          {status === CompanyCardStatus.SettingUp && (
            <Tag color="processing" data-testid="company-card-status-setting-up">
              {t('labels.setting_up')}
            </Tag>
          )}
          {Boolean(onEdit) && <Button onClick={onEdit} type="text" icon={<EditOutlined />} />}
          {Boolean(onDelete) && (
            <Popconfirm
              title={t('delete_confirmation')}
              onConfirm={onDelete}
              okText={t('buttons.confirm')}
              cancelText={t('buttons.cancel')}
              okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Flex>
      }
      classNames={{ body: '!py-md' }}
    >
      <p>
        {AddressHelpers.format(
          { ...company, street_name: company.address },
          company.regional_config?.address_format ?? 'international',
        )}
      </p>
      {isDraft && (
        <>
          <p className="mt-xs">
            {company.google_place ? <CheckOutlined /> : <CloseOutlined />} {t('labels.google_place')}
          </p>
          <p className="mt-xs">
            {company.import_menu ? <CheckOutlined /> : <CloseOutlined />} {t('labels.menu_import')}{' '}
            {company.import_menu && Boolean(company.menu_provider) ? `(${company.menu_provider})` : null}
          </p>
        </>
      )}
      {status === CompanyCardStatus.SettingUp && onContinueSetup && (
        <Button
          type="primary"
          size="small"
          className="mt-sm"
          data-testid="company-card-continue-setup"
          onClick={() => onContinueSetup(company.id)}
        >
          {t('buttons.continue_setup')}
        </Button>
      )}
    </Card>
  );
}
